// src/screens/admin/customRequestDetail/CustomRequestDetail.jsx

import { useState, useRef, useEffect, useContext } from "react";
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Mail,
  Phone,
  ImageIcon,
  MessageSquare,
  Tag,
  ChevronDown,
  Check,
  AlertTriangle,
  DollarSign,
  Calendar,
  Lock,
  PackageCheck,
  PhoneCall,
  Package,
  Plus,
  Trash2,
  Pencil,
  Save,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/css/admin/adminCustomOrderDetail/AdminCustomOrderDetail.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

// ─────────────────────────────────────────────────────────────
// CONSTANTS — status keys match backend values (lowercase)
// ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS = ["pending", "quoted", "accepted", "rejected"];

const STATUS_CONFIG = {
  pending: {
    label: "Quote Pending",
    icon: Clock,
    className: "crd-status--pending",
  },
  quoted: {
    label: "Quote Sent",
    icon: Send,
    className: "crd-status--sent",
  },
  accepted: {
    label: "Confirmed",
    icon: CheckCircle,
    className: "crd-status--confirmed",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    className: "crd-status--rejected",
  },
};

const REQUEST_FLOW = ["Request Received", "Under Review", "quoted", "accepted"];

const nowString = () => {
  const d = new Date();
  return (
    d.toLocaleDateString("en-PK", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }) +
    " · " +
    d.toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" })
  );
};

const getTimelineDoneCount = (status) => {
  if (status === "pending") return 2;
  if (status === "quoted") return 3;
  if (status === "accepted") return 4;
  return 2;
};

const buildRequestTimeline = (prevTimeline, newStatus) => {
  if (newStatus === "rejected") {
    return [
      ...prevTimeline.filter((t) => t.done && t.status !== "rejected"),
      { status: "rejected", date: nowString(), done: true },
    ];
  }
  const doneCount = getTimelineDoneCount(newStatus);
  return REQUEST_FLOW.map((step, i) => {
    const existing = prevTimeline.find((t) => t.status === step);
    const isDone = i < doneCount;
    const isNew = isDone && !existing?.done;
    return {
      status: step,
      done: isDone,
      date:
        existing?.done && existing?.date && existing.date !== "—"
          ? existing.date
          : isNew
            ? nowString()
            : "—",
    };
  });
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE — looks up STATUS_CONFIG by lowercase key
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`crd-status-badge ${config.className}`}>
      {Icon && <Icon size={12} />}
      {config.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS DROPDOWN
// ─────────────────────────────────────────────────────────────
const StatusDropdown = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div className="crd-status-dropdown-wrapper" ref={wrapperRef}>
      <button
        className={`crd-status-dropdown-btn ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setIsOpen((p) => !p)}
      >
        <StatusBadge status={value} />
        {!disabled && (
          <ChevronDown
            size={14}
            className={`crd-chevron ${isOpen ? "open" : ""}`}
          />
        )}
      </button>
      {isOpen && (
        <div className="crd-status-menu">
          {STATUS_OPTIONS.map((s) => (
            <div
              key={s}
              className={`crd-status-option ${value === s ? "active" : ""}`}
              onClick={() => {
                onChange(s);
                setIsOpen(false);
              }}
            >
              <StatusBadge status={s} />
              {value === s && <Check size={13} className="crd-status-check" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TIMELINE
// ─────────────────────────────────────────────────────────────
const Timeline = ({ steps }) => (
  <div className="crd-timeline">
    {steps.map((step, i) => {
      const label = STATUS_CONFIG[step.status]?.label || step.status;
      return (
        <div
          key={i}
          className={`crd-timeline-step ${step.done ? "done" : "pending"}`}
        >
          <div className="crd-timeline-left">
            <div className="crd-timeline-dot" />
            {i < steps.length - 1 && <div className="crd-timeline-line" />}
          </div>
          <div className="crd-timeline-content">
            <p className="crd-timeline-label">{label}</p>
            <p className="crd-timeline-date">{step.date}</p>
          </div>
        </div>
      );
    })}
  </div>
);

// ─────────────────────────────────────────────────────────────
// REJECT MODAL
// ─────────────────────────────────────────────────────────────
const RejectModal = ({ requestId, onConfirm, onClose, isRejecting }) => {
  console.log("Modal render, isRejecting:", isRejecting);
  return (
    <div
      className="crd-modal-overlay"
      onClick={!isRejecting ? onClose : undefined}
    >
      <div className="crd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crd-modal-icon-wrap">
          <AlertTriangle size={28} className="crd-modal-icon" />
        </div>
        <h3 className="crd-modal-title">Reject Request?</h3>
        <p className="crd-modal-desc">
          Are you sure you want to reject <strong>{requestId}</strong>? The
          customer will be notified that their request was not accepted.
        </p>
        <div className="crd-modal-actions">
          <button
            className="crd-modal-btn crd-modal-btn--secondary"
            onClick={onClose}
            disabled={isRejecting}
          >
            Go Back
          </button>
          <button
            className="crd-modal-btn crd-modal-btn--danger"
            onClick={onConfirm}
            disabled={isRejecting}
          >
            {isRejecting ? (
              <span className="crd-modal-spinner" />
            ) : (
              <>
                <XCircle size={14} /> Yes, Reject
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MATERIALS CARD
// ─────────────────────────────────────────────────────────────
const MaterialsCard = ({ initialMaterials, baseUrl, authToken, cart }) => {
  // existing attached materials — same shape as API response (productmaterials)
  const [attachedMaterials, setAttachedMaterials] = useState(
    initialMaterials || [],
  );
  const [editing, setEditing] = useState(false);
  const [snapshot, setSnapshot] = useState(initialMaterials || []);

  // dropdowns for category/material — same pattern as AddProduct
  const [allCategoriesForMaterial, setAllCategoriesForMaterial] = useState([]);
  const [allMaterial, setAllMaterial] = useState([]);
  const [pmCategory, setPmCategory] = useState("");
  const [material, setMaterial] = useState(""); // material names in dropdown
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [isAttaching, setIsAttaching] = useState(false);
  const [pmErrors, setPmErrors] = useState({});

  // useEffect(() => {
  //   setMaterials(initialMaterials || []);
  //   setSnapshot(initialMaterials || []);
  // }, [initialMaterials]);

  // ── fetch categories  ──
  const viewCategories = () => {
    axios
      .get(`${baseUrl}/admin/category/viewCategories`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { type: "inventory" },
      })
      .then((response) => {
        setAllCategoriesForMaterial(response.data.categories);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── fetch materials against selected category ──
  const viewAllMaterials = () => {
    axios
      .get(`${baseUrl}/admin/inventory/viewInventory`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { adcategory_id: pmCategory },
      })
      .then((response) => {
        setAllMaterial(response.data.allMaterial);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  //function to get unit of selected material
  const viewMaterialUnit = (id) => {
    axios
      .get(`${baseUrl}/admin/products/viewMaterialUnit`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { id: id },
      })
      .then((response) => {
        console.log(response.data);
        setUnit(response.data.unit);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  //function to view attached materials of product/order
  const viewAttachedMaterial = () => {
    axios
      .get(`${baseUrl}/admin/order/viewOrderMaterial/${cart.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log("api material data: ", response.data);
        setAttachedMaterials(response.data.cartMaterial);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (editing) viewCategories();
  }, [editing]);

  useEffect(() => {
    if (pmCategory) viewAllMaterials();
  }, [pmCategory]);

  useEffect(() => {
    if (material) viewMaterialUnit(material);
  }, [material]);

  useEffect(() => {
    viewAttachedMaterial();
  }, []);

  // ── attach new material —  ──
  const handleAttachMaterial = () => {
    const errs = {};
    if (!material) errs.material = "Material is required";
    if (!quantity || Number(quantity) <= 0)
      errs.quantity = "Valid quantity is required";
    if (Object.keys(errs).length > 0) {
      setPmErrors(errs);
      return;
    }

    setIsAttaching(true);

    const formdata = new FormData();
    formdata.append("admaterial_id", material);
    formdata.append("qty", quantity);

    axios
      .post(`${baseUrl}/admin/products/addProductMaterial`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        const selectedMaterial = allMaterial.find((m) => m.id === material);
        // setAttachedMaterials((prev) => [
        //   ...prev,
        //   {
        //     id: response.data?.productMaterial?.id || Date.now(),
        //     admaterial_id: material,
        //     qty: quantity,
        //     admaterial: selectedMaterial,
        //   },
        // ]);
        setIsAttaching(false);
        setPmCategory("");
        setMaterial("");
        setQuantity("");
        setUnit("");
        setPmErrors({});
      })
      .catch((error) => {
        console.log(error);
        setIsAttaching(false);
      });
  };

  // ── delete attached material ──
  const handleDeleteMaterial = (id) => {
    // setAttachedMaterials((prev) => prev.filter((m) => m.id !== rowId));
    const formdata = new FormData();
    formdata.append("id", id);
    axios
      .post(`${baseUrl}/admin/order/deleteOrderMaterial`, formdata, {
        headers: { Authorization: `Bearer${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        viewAttachedMaterial();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleEdit = () => {
    setSnapshot(attachedMaterials);
    setEditing(true);
  };

  const updateMaterial = () => {
    const errs = {};
    if (!material) errs.material = "Material is required";
    if (!quantity || Number(quantity) <= 0)
      errs.quantity = "Valid quantity is required";
    if (Object.keys(errs).length > 0) {
      setPmErrors(errs);
      return;
    }

    setIsAttaching(true);

    const formData = new FormData();
    formData.append("adcart_id", cart.id);
    formData.append("admaterial_id", material);
    formData.append("qty", quantity);
    formData.append("is_attach", 1); // not necessary field

    axios
      .post(`${baseUrl}/admin/order/updateOrderMaterial`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        viewAttachedMaterial();
        setIsAttaching(false);
        setPmCategory("");
        setMaterial("");
        setQuantity("");
        setUnit("");
        setPmErrors({});
      })
      .catch((error) => {
        console.log(error);
        setIsAttaching(false);
      });
  };

  const handleCancel = () => {
    setAttachedMaterials(snapshot);
    setEditing(false);
    setPmCategory("");
    setMaterial("");
    setQuantity("");
    setUnit("");
    setPmErrors({});
  };

  const handleDone = () => {
    setSnapshot(attachedMaterials);
    setEditing(false);
    setPmCategory("");
    setMaterial("");
    setQuantity("");
    setUnit("");
    setPmErrors({});
  };

  return (
    <div className="crd-card crd-materials-card">
      <h2 className="crd-card-title">
        <Package size={16} className="crd-card-icon" />
        Materials
        <span className="crd-materials-count">
          {attachedMaterials.length} items
        </span>
        <div className="crd-materials-header-actions">
          {editing ? (
            <button
              className="crd-mat-btn crd-mat-btn--save"
              onClick={handleDone}
            >
              <Check size={13} /> Done
            </button>
          ) : (
            <button
              className="crd-mat-btn crd-mat-btn--edit"
              onClick={handleEdit}
            >
              <Pencil size={13} /> Edit
            </button>
          )}
        </div>
      </h2>

      <div className="crd-materials-table">
        <div className={`crd-mat-thead ${editing ? "editing" : ""}`}>
          <span>#</span>
          <span>Material Name</span>
          <span>Quantity</span>
          <span>Unit</span>
          {editing && <span />}
        </div>

        {attachedMaterials.map((mat, i) => (
          <div
            key={mat.id}
            className={`crd-mat-row ${editing ? "editing" : ""}`}
          >
            <span className="crd-mat-idx">{i + 1}</span>
            <span className="crd-mat-name">{mat.admaterial?.name}</span>
            <span className="crd-mat-qty">{mat.qty}</span>
            <span className="crd-mat-unit">{mat.admaterial?.unit}</span>
            {editing && (
              <button
                className="crd-mat-del"
                onClick={() => handleDeleteMaterial(mat.id)}
                title="Remove"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}

        {attachedMaterials.length === 0 && !editing && (
          <div className="crd-mat-empty">No materials added yet.</div>
        )}
      </div>

      {editing && (
        <div className="crd-mat-add-card">
          <div className="crd-mat-add-grid">
            <div className="crd-mat-add-field">
              <label className="crd-mat-add-label">Category</label>
              <select
                className="crd-mat-add-select"
                value={pmCategory}
                onChange={(e) => {
                  setPmCategory(e.target.value);
                  // setMaterial("");
                  // setUnit("");
                }}
              >
                <option value="">Select category…</option>
                {allCategoriesForMaterial.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="crd-mat-add-field">
              <label className="crd-mat-add-label">Material</label>
              <select
                className={`crd-mat-add-select ${pmErrors.material ? "crd-mat-add--error" : ""}`}
                value={material}
                disabled={!pmCategory}
                onChange={(e) => {
                  const selected = allMaterial.find(
                    (item) => item.id === e.target.value,
                  );
                  setMaterial(e.target.value);
                  setUnit(selected?.unit || "");
                  if (pmErrors.material)
                    setPmErrors((p) => ({ ...p, material: "" }));
                }}
              >
                <option value="">Select material…</option>
                {allMaterial.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {pmErrors.material && (
                <span className="crd-mat-add-error">{pmErrors.material}</span>
              )}
            </div>

            <div className="crd-mat-add-field">
              <label className="crd-mat-add-label">Qty</label>
              <input
                className={`crd-mat-add-input ${pmErrors.quantity ? "crd-mat-add--error" : ""}`}
                type="number"
                min="0"
                placeholder="0"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  if (pmErrors.quantity)
                    setPmErrors((p) => ({ ...p, quantity: "" }));
                }}
              />
              {pmErrors.quantity && (
                <span className="crd-mat-add-error">{pmErrors.quantity}</span>
              )}
            </div>

            <div className="crd-mat-add-field">
              <label className="crd-mat-add-label">Unit</label>
              <input
                className="crd-mat-add-input crd-mat-add-input--readonly"
                value={unit}
                readOnly
                placeholder="—"
                tabIndex={-1}
              />
            </div>

            <div className="crd-mat-add-field crd-mat-add-attach-wrap">
              <label className="crd-mat-add-label" aria-hidden="true">
                &nbsp;
              </label>
              <button
                type="button"
                className="crd-mat-attach-btn"
                onClick={updateMaterial}
                disabled={isAttaching}
                title="Attach material"
              >
                {isAttaching ? (
                  <span className="crd-btn-spinner" />
                ) : (
                  <Plus size={16} />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const CustomRequestDetail = () => {
  const { id } = useParams();
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const navigate = useNavigate();

  const [quotedPrice, setQuotedPrice] = useState("");
  const [advancePayment, setAdvancePayment] = useState("");
  const [estimatedDays, setEstimatedDays] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [req, setReq] = useState(null);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    viewDeatils();
  }, [id]);

  const viewDeatils = () => {
    axios
      .get(`${baseUrl}/admin/order/customRequestDetails`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { id },
      })
      .then((response) => {
        console.log(response.data.requestDetails);
        setReq(response.data.requestDetails);
        setTimeline(buildInitialTimeline(response.data.requestDetails.status));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // status param now expected lowercase: pending | quoted | accepted | rejected
  const buildInitialTimeline = (status) => {
    const doneCount = getTimelineDoneCount(status);
    if (status === "rejected") {
      return [
        { status: "Request Received", date: "—", done: true },
        { status: "Under Review", date: "—", done: true },
        { status: "rejected", date: nowString(), done: true },
      ];
    }
    return REQUEST_FLOW.map((step, i) => ({
      status: step,
      done: i < doneCount,
      date: "—",
    }));
  };

  const [isSendingQuote, setIsSendingQuote] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  if (!req) {
    return <div>Loading...</div>;
  }

  // ── status checks now use lowercase backend keys ──
  const isConfirmed = req.status === "accepted";
  const isRejectedStatus = req.status === "rejected";
  const isFinal = isConfirmed || isRejectedStatus;
  const canSendQuote = req.status === "pending" || req.status === "quoted";

  const SendQuote = () => {
    if (!quotedPrice) return;
    setIsSendingQuote(true);

    const formdata = new FormData();
    formdata.append("quotedPrice", quotedPrice);
    formdata.append("adminNote", adminNote);
    formdata.append("id", id);

    axios
      .post(`${baseUrl}/admin/order/sendQuote`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setIsSendingQuote(false);
        viewDeatils();
      })
      .catch((error) => {
        console.log(error);
        setIsSendingQuote(false);
      });
  };

  const updateStatus = (newStatus) => {
    setStatusUpdating(true);

    const formdata = new FormData();
    formdata.append("id", id);
    formdata.append("status", newStatus);

    axios
      .post(`${baseUrl}/admin/order/updateStatus`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log("status api called");
        console.log(response.data);
        setStatusUpdating(false);
        setIsRejecting(false);
        setShowRejectModal(false);
        viewDeatils();
      })
      .catch((error) => {
        console.log(error);
        setStatusUpdating(false);
        setIsRejecting(false);
        setShowRejectModal(false);
      });
  };

  const handleStausChange = (newStatus) => {
    if (newStatus === req.status) return;
    if (newStatus === "rejected") {
      setShowRejectModal(true);
      return;
    }
    updateStatus(newStatus);
  };

  const handleRejectConfirm = () => {
    setIsRejecting(true);
    console.log("reject called.");
    updateStatus("rejected");
  };

  return (
    <div className="crd-container">
      {/* ── Header ── */}
      <div className="crd-header">
        <div className="crd-header-left">
          <button className="crd-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="crd-header-top-row">
              <h1 className="crd-title">Request {req?.id}</h1>
              <StatusBadge status={req?.status} />
            </div>
            <p className="crd-subtitle">
              Submitted on{" "}
              {new Date(req?.created_at).toLocaleDateString("en-PK", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        </div>
        <div className="crd-header-right">
          <div className="crd-header-actions">
            {!isFinal && (
              <button
                className="crd-reject-btn"
                onClick={() => setShowRejectModal(true)}
              >
                <XCircle size={14} /> Reject Request
              </button>
            )}
            <div>
              <p className="crd-change-label">
                {isFinal ? "Request Status" : "Update Status"}
              </p>
              <StatusDropdown
                value={req?.status}
                onChange={handleStausChange}
                disabled={isFinal}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Toasts ── */}
      {statusUpdating && (
        <div className="crd-updating-toast">
          <span className="crd-toast-spinner" /> Updating status...
        </div>
      )}
      {quoteSent && (
        <div className="crd-updating-toast crd-toast--success">
          <CheckCircle size={14} /> Quote sent to customer!
        </div>
      )}

      {/* ── Confirmed Banner ── */}
      {isConfirmed && (
        <div className="crd-confirmed-banner">
          <PackageCheck size={18} />
          <div>
            <strong>Quote Accepted — Order Created!</strong>
            <span>
              {req.linkedOrderId ? (
                <>
                  {" "}
                  Order{" "}
                  <span
                    className="crd-banner-order-id"
                    onClick={() =>
                      navigate(`/admin/order-list/${req.linkedOrderId}`)
                    }
                  >
                    {req.linkedOrderId}
                  </span>{" "}
                  successfully created.
                </>
              ) : (
                " Customer ne quote accept kar liya. Order list mein naya order appear ho ga."
              )}
            </span>
          </div>
          {req.linkedOrderId && (
            <button
              className="crd-banner-btn"
              onClick={() => navigate(`/admin/order-list/${req.linkedOrderId}`)}
            >
              View Order
            </button>
          )}
        </div>
      )}

      {/* ── Main Grid ── */}
      <div className="crd-grid">
        {/* ════ LEFT COLUMN ════ */}
        <div className="crd-col-left">
          {/* Request Details */}
          <div className="crd-card">
            <h2 className="crd-card-title">
              <MessageSquare size={16} className="crd-card-icon" /> Request
              Details
            </h2>
            <div className="crd-meta-grid">
              <div className="crd-meta-item">
                <p className="crd-meta-label">Furniture Type</p>
                <span className="crd-type-badge">{req.adproduct?.name}</span>
              </div>

              {req.dimensions && (
                <div className="crd-meta-item">
                  <p className="crd-meta-label">Dimensions</p>
                  <p className="crd-meta-value">{req.dimensions}</p>
                </div>
              )}
              <div className="crd-meta-item">
                <p className="crd-meta-label">Reference Images</p>
                <div className="crd-images-count-inline">
                  <ImageIcon size={13} />
                  <span>{req.custom_cart_images?.length} images</span>
                </div>
              </div>
            </div>
            <div className="crd-description-block">
              <p className="crd-meta-label" style={{ marginBottom: "0.5rem" }}>
                Customer Description
              </p>
              <p className="crd-desc-text">"{req.description}"</p>
            </div>
            {req.custom_cart_images?.length > 0 && (
              <div className="crd-ref-images">
                <p
                  className="crd-meta-label"
                  style={{ marginBottom: "0.65rem" }}
                >
                  Reference Images ({req.custom_cart_images?.length})
                </p>
                <div className="crd-ref-images-grid">
                  {req.custom_cart_images?.map((img, i) => (
                    <a
                      key={i}
                      href={`${baseUrlForFile}${img.image_path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="crd-ref-image-wrap"
                    >
                      <img
                        src={`${baseUrlForFile}${img.image_path}`}
                        alt={`ref-${i}`}
                        className="crd-ref-image"
                      />
                      <div className="crd-ref-image-overlay">View</div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Send Quote */}
          {canSendQuote && (
            <div className="crd-card crd-quote-card">
              <h2 className="crd-card-title">
                <DollarSign size={16} className="crd-card-icon" />
                {req.status === "quoted" ? "Update Quote" : "Send Quote"}
              </h2>
              <div className="crd-quote-form">
                <div className="crd-form-row">
                  <div className="crd-form-group">
                    <label className="crd-form-label">
                      Quoted Price (PKR) *
                    </label>
                    <input
                      type="number"
                      className="crd-form-input"
                      placeholder="e.g. 75000"
                      value={quotedPrice}
                      onChange={(e) => setQuotedPrice(e.target.value)}
                    />
                  </div>
                  {/* <div className="crd-form-group">
                    <label className="crd-form-label">
                      <DollarSign size={13} /> Advance Payment (PKR)
                      <span className="crd-form-label-sub">Optional</span>
                    </label>
                    <input
                      type="number"
                      className="crd-form-input"
                      placeholder="e.g. 37500"
                      value={advancePayment}
                      onChange={(e) => setAdvancePayment(e.target.value)}
                    />
                  </div> */}
                </div>
                {/* <div className="crd-form-row">
                  <div className="crd-form-group">
                    <label className="crd-form-label">
                      <Calendar size={13} /> Estimated Days *
                    </label>
                    <input
                      type="number"
                      className="crd-form-input"
                      placeholder="e.g. 21"
                      value={estimatedDays}
                      onChange={(e) => setEstimatedDays(e.target.value)}
                    />
                  </div>
                  <div className="crd-form-group" />
                </div> */}
                <div className="crd-form-group">
                  <label className="crd-form-label">
                    Admin Note (shown to customer)
                  </label>
                  <textarea
                    className="crd-form-textarea"
                    placeholder="e.g. Sheesham wood available, delivery in 21 days..."
                    rows={3}
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                  />
                </div>
                <button
                  className="crd-send-quote-btn"
                  onClick={SendQuote}
                  disabled={isSendingQuote || !quotedPrice || !estimatedDays}
                >
                  {isSendingQuote ? (
                    <>
                      <span className="crd-btn-spinner" /> Sending...
                    </>
                  ) : (
                    <>
                      <Send size={14} />{" "}
                      {req.status === "quoted"
                        ? "Update Quote"
                        : "Send Quote to Customer"}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Read-only quote — after confirmed */}
          {isConfirmed && (
            <div className="crd-card crd-quote-readonly-card">
              <h2 className="crd-card-title">
                <Lock size={16} className="crd-card-icon" /> Quote — Locked
              </h2>
              <div className="crd-quote-readonly-grid">
                <div>
                  <p className="crd-meta-label">Quoted Price</p>
                  <p className="crd-quote-readonly-price">
                    PKR {req.quotedPrice?.toLocaleString() || "—"}
                  </p>
                </div>
                <div>
                  {/* <p className="crd-meta-label">Advance Payment</p>
                  <p
                    className="crd-quote-readonly-price"
                    style={{ color: "#5c6b47" }}
                  >
                    {req.advancePayment
                      ? `PKR ${req.advancePayment.toLocaleString()}`
                      : "—"}
                  </p>
                </div>
                <div> */}
                  {/* <p className="crd-meta-label">Estimated Days</p>
                  <p className="crd-quote-readonly-days">
                    {req.estimatedDays ? `${req.estimatedDays} days` : "—"}
                  </p> */}
                </div>
              </div>
              {req.adminNote && (
                <div className="crd-quote-readonly-note">
                  <Tag size={13} style={{ color: "#d67e43", flexShrink: 0 }} />
                  <p>{req.adminNote}</p>
                </div>
              )}
              <p className="crd-quote-lock-info">
                <Lock size={11} /> Quote accepted by customer — price cannot be
                changed.
              </p>
            </div>
          )}

          {/* ── Materials Card ── */}
          <MaterialsCard
            initialMaterials={[]}
            baseUrl={baseUrl}
            authToken={authToken}
            cart={req}
          />

          {/* Admin note — rejected */}
          {isRejectedStatus && req.adminNote && (
            <div className="crd-card crd-note-card">
              <h2 className="crd-card-title">
                <Tag size={16} className="crd-card-icon" /> Admin Note
              </h2>
              <p className="crd-note-text">{req.adminNote}</p>
            </div>
          )}

          {/* Timeline */}
          <div className="crd-card">
            <h2 className="crd-card-title">
              <Clock size={16} className="crd-card-icon" /> Request Timeline
            </h2>
            <Timeline steps={timeline} />
          </div>
        </div>

        {/* ════ RIGHT COLUMN ════ */}
        <div className="crd-col-right">
          {/* ── Customer Card ── */}
          <div className="crd-card">
            <h2 className="crd-card-title">Customer</h2>
            <div className="crd-customer-top">
              <div className="crd-avatar">
                {req.adcustomer?.fullName
                  ?.split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </div>
              <div className="crd-customer-info">
                <p className="crd-customer-name">{req.adcustomer?.fullName}</p>
                <div className="crd-customer-email-row">
                  <Mail size={12} className="crd-info-icon" />
                  <span className="crd-customer-email">
                    {req.adcustomer?.email}
                  </span>
                </div>
              </div>
            </div>
            <div className="crd-customer-divider" />
            <div className="crd-call-block">
              <div className="crd-phone-row">
                <Phone size={14} className="crd-info-icon" />
                <span className="crd-phone-number">{req.phoneNo}</span>
              </div>
              <a href={`tel:${req.phoneNo}`} className="crd-call-cta">
                <PhoneCall size={14} />
                Call for Details
              </a>
            </div>
          </div>

          {/* Quote Summary */}
          <div className="crd-card crd-summary-card">
            <h2 className="crd-card-title">Quote Summary</h2>
            <div className="crd-summary-list">
              <div className="crd-summary-row">
                <span className="crd-summary-label">Request ID</span>
                <span className="crd-summary-id">{req.id}</span>
              </div>
              <div className="crd-summary-row">
                <span className="crd-summary-label">Furniture Type</span>
                <span className="crd-summary-value">{req.adproduct?.name}</span>
              </div>

              <div className="crd-summary-row">
                <span className="crd-summary-label">Quoted Price</span>
                <span className="crd-summary-value crd-summary-price">
                  {req.quotedPrice
                    ? `PKR ${req.quotedPrice.toLocaleString()}`
                    : "Not quoted yet"}
                </span>
              </div>
              <div className="crd-summary-row">
                {/* <span className="crd-summary-label">Advance Payment</span> */}
                {/* <span
                  className="crd-summary-value"
                  style={{
                    color: req.advancePayment ? "#5c6b47" : undefined,
                    fontWeight: req.advancePayment ? 700 : 400,
                  }}
                >
                  {req.advancePayment
                    ? `PKR ${req.advancePayment.toLocaleString()}`
                    : "—"}
                </span> */}
              </div>
              <div className="crd-summary-row">
                {/* <sspan className="crd-summary-label">Est. Delivery</span> */}
                {/* <span className="crd-summary-value">
                  {req.estimatedDays ? `${req.estimatedDays} days` : "—"}
                </span> */}
              </div>
              <div className="crd-summary-row">
                <span className="crd-summary-label">Status</span>
                <StatusBadge status={req.status} />
              </div>
              {req.linkedOrderId && (
                <div className="crd-summary-row">
                  <span className="crd-summary-label">Linked Order</span>
                  <span
                    className="crd-summary-id"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/admin/order-list/${req.linkedOrderId}`)
                    }
                  >
                    {req.linkedOrderId}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Reject Modal ── */}
      {showRejectModal && (
        <RejectModal
          requestId={req.id}
          onConfirm={handleRejectConfirm}
          onClose={() => !isRejecting && setShowRejectModal(false)}
          isRejecting={isRejecting}
        />
      )}
    </div>
  );
};

export default CustomRequestDetail;
